import { useEffect, useRef, useState } from 'react'
import { UseFormSetValue } from 'react-hook-form'
import toast from 'react-hot-toast'
import { Button } from 'components/common'
import { CloseIcon, UploadIcon } from 'components/icons'
import { usePresignedUpload } from 'hooks'
import { compareMultipleFileExtension } from 'utils/helpers'
import { handleError } from 'utils/helpers/handleError'
import {
  FRESH_CATEGORY_IMAGE_TYPES,
  FRESH_CATEGORY_UPLOAD_MODULE,
  freshCategoryFormFields,
} from './utils'

const { IMAGE } = freshCategoryFormFields

type Props = {
  setValue: UseFormSetValue<any>
  // S3 key already saved on the category (edit page)
  defaultKey?: string
  // Signed, viewable URL for that key, returned by the detail endpoint
  defaultUrl?: string | null
  onUploadingChange: (isUploading: boolean) => void
}

// The presigned key is `<dir>/<timestamp>-<fileName>` - show just the file name
const fileNameFromKey = (key: string) =>
  key.split('/').pop()?.replace(/^\d+-/, '') || key

/**
 * Uploads the picked image straight to S3 through the presigned-URL flow and
 * stores the returned key on the form's `image` field.
 *
 * The category keeps only the S3 key, so the saved image is rendered from the
 * signed `imageUrl` the detail endpoint returns alongside it; a newly picked
 * one is previewed from the local file.
 *
 * THE UPLOAD IS NOT THE SAVE. The file reaches S3 as soon as it is picked,
 * but the category still points at the old key until the form is submitted -
 * so a picked or removed image is marked as pending rather than reported as
 * done, which would leave the user thinking a change they never submitted had
 * been applied.
 */
const CategoryImageField = ({
  setValue,
  defaultKey = '',
  defaultUrl = null,
  onUploadingChange,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [imageKey, setImageKey] = useState<string>(defaultKey)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  // Set once the user picks or removes an image in this session - what makes
  // the change pending, and what stops the saved value overwriting it below.
  const [isPending, setIsPending] = useState<boolean>(false)

  // The edit page renders the form before the category has loaded, so the
  // saved key arrives after mount and has to be adopted when it does.
  useEffect(() => {
    if (isPending) return

    setImageKey(defaultKey)
  }, [defaultKey, isPending])

  const { uploadProgress, isUploading, uploadAndProcess } = usePresignedUpload({
    module: FRESH_CATEGORY_UPLOAD_MODULE,
    // Nothing to process server-side - the key is saved with the category
    onProcess: async (key) => key,
  })

  useEffect(() => {
    onUploadingChange(isUploading)
  }, [isUploading])

  // Release the local preview when it is replaced or the field unmounts
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  const handleFileChange = async ({
    target,
  }: React.ChangeEvent<HTMLInputElement>) => {
    const file = target.files?.[0]
    target.value = ''

    if (!file) return

    if (!compareMultipleFileExtension(file, FRESH_CATEGORY_IMAGE_TYPES)) {
      toast.error('Invalid file format', { duration: 3000 })
      return
    }

    const toastId = toast.loading('Uploading image...', { duration: Infinity })
    try {
      const key = (await uploadAndProcess(file)) as string
      setImageKey(key)
      setPreviewUrl(URL.createObjectURL(file))
      setIsPending(true)
      setValue(IMAGE.key, key, { shouldDirty: true })
      // Deliberately not "uploaded" - nothing has changed on the category yet
      toast.success('Image selected. Submit the form to save it.', {
        id: toastId,
        duration: 3000,
      })
    } catch (error) {
      handleError(error, toastId, { default: 'Failed to upload image.' })
    }
  }

  const removeImage = (e: React.MouseEvent) => {
    e.stopPropagation()
    setImageKey('')
    setPreviewUrl(null)
    setIsPending(true)
    setValue(IMAGE.key, '', { shouldDirty: true })
  }

  // A locally picked file wins; otherwise the signed URL of the saved image,
  // which only applies while the field still holds that saved key.
  const displayUrl = previewUrl ?? (imageKey === defaultKey ? defaultUrl : null)

  return (
    <div>
      <p className="mb-1 text-xs font-semibold text-sidebar-primary">
        {IMAGE.label}
      </p>

      <input
        className="hidden"
        type="file"
        onChange={handleFileChange}
        accept={FRESH_CATEGORY_IMAGE_TYPES.join(',')}
        ref={inputRef}
      />

      <div
        className={`relative flex h-44 w-44 items-center justify-center border border-dashed border-slate-500 transition-colors hover:border-slate-600 ${
          isUploading ? 'cursor-wait' : 'cursor-pointer'
        }`}
        onClick={() => !isUploading && inputRef.current?.click()}
      >
        {isUploading ? (
          <span className="text-sm text-slate-600">
            Uploading {uploadProgress ?? 0}%
          </span>
        ) : imageKey ? (
          <>
            {displayUrl ? (
              <img
                src={displayUrl}
                alt="Category"
                className="h-full w-full object-contain p-1"
              />
            ) : (
              // Only when the saved image cannot be signed - it has been
              // removed from S3, or the key is stale
              <span className="break-all p-3 text-center text-xs text-slate-600">
                {fileNameFromKey(imageKey)}
              </span>
            )}
            <Button
              onClick={removeImage}
              type="button"
              variant="custom"
              icon2={<CloseIcon />}
              icon2ClassName="!h-3.5 !w-3.5 text-[#161C28]"
              className="absolute right-0 top-0 bg-gray-50 p-1"
            />
          </>
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-slate-200 p-2">
            <div className="flex flex-col items-center gap-3 text-center text-slate-600">
              <div className="size-8">
                <UploadIcon />
              </div>
              <span className="text-sm">Click to upload image</span>
            </div>
          </div>
        )}
      </div>

      {isPending && !isUploading && (
        <p className="mt-1 text-xs font-medium text-amber-600">
          {imageKey
            ? 'New image selected - not saved until you submit.'
            : 'Image removed - not saved until you submit.'}
        </p>
      )}

      <p className="mt-1 text-xs text-gray-500">
        PNG, JPG or WEBP. Shown for this category in the Fresh mobile app.
      </p>
    </div>
  )
}

export default CategoryImageField
